import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type {
  CardSeparator,
  DeckVisibility,
  FieldDelimiter,
  ImportPreviewResponse,
  ParseConfig,
  ParseWarning,
} from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button } from '../../components'
import { ImportApiError, commitImport, previewImport } from '../../lib/import-api.js'
import styles from './ImportQuizletPage.module.css'

// ── Auto-detect helpers (pure, browser-safe) ─────────────────────────────────

function countChar(str: string, ch: string): number {
  let n = 0
  for (let i = 0; i < str.length; i++) {
    if (str[i] === ch) n++
  }
  return n
}

function detectFormat(input: string): 'csv' | 'tsv' | 'delimited' | 'unknown' {
  const sniff = input.slice(0, 2048)
  const tabs = countChar(sniff, '\t')
  const commas = countChar(sniff, ',')
  if (tabs > 0 && tabs >= commas) return 'tsv'
  if (commas > 0 && commas > tabs * 2) return 'csv'
  if (tabs > 0 || commas > 0) return 'delimited'
  return 'unknown'
}

function suggestConfig(input: string): ParseConfig {
  const sniff = input.slice(0, 2048)
  const format = detectFormat(input)
  if (format === 'tsv') {
    return { cardSeparator: 'newline', fieldDelimiter: 'tab', normalizeSmartPunctuation: true }
  }
  if (format === 'csv') {
    return { cardSeparator: 'newline', fieldDelimiter: 'comma', normalizeSmartPunctuation: true }
  }
  const hasSemicolonPair = sniff.includes(';;')
  const hasDoubleNewline = sniff.includes('\n\n')
  const hasTabs = countChar(sniff, '\t') > 0
  const hasPipes = countChar(sniff, '|') > 0
  const hasHyphens = / - /.test(sniff)

  const cardSeparator: CardSeparator = hasSemicolonPair
    ? 'semicolon-pair'
    : hasDoubleNewline
      ? 'double-newline'
      : 'newline'

  let fieldDelimiter: FieldDelimiter = 'tab'
  if (!hasTabs) {
    if (hasPipes) fieldDelimiter = 'pipe'
    else if (hasHyphens) fieldDelimiter = 'hyphen'
    else fieldDelimiter = 'hyphen'
  }

  return { cardSeparator, fieldDelimiter, normalizeSmartPunctuation: true }
}

// ── State machine ─────────────────────────────────────────────────────────────

interface PreviewSnapshot {
  input: string
  config: ParseConfig
}

type PageState =
  | { phase: 'idle' }
  | { phase: 'previewing' }
  | { phase: 'previewed'; preview: ImportPreviewResponse; snapshot: PreviewSnapshot }
  | { phase: 'committing'; preview: ImportPreviewResponse; snapshot: PreviewSnapshot }
  | { phase: 'committed'; deckId: string; cardCount: number }
  | { phase: 'error'; message: string }

// ── Component ─────────────────────────────────────────────────────────────────

export default function ImportQuizletPage(): JSX.Element {
  const { firebaseUser } = useAuth()

  // Source tabs
  const [activeTab, setActiveTab] = useState<'paste' | 'upload'>('paste')
  const [inputText, setInputText] = useState('')
  const pasteTabRef = useRef<HTMLButtonElement>(null)
  const uploadTabRef = useRef<HTMLButtonElement>(null)

  // Parse config
  const [cardSeparator, setCardSeparator] = useState<CardSeparator>('double-newline')
  const [fieldDelimiter, setFieldDelimiter] = useState<FieldDelimiter>('tab')
  const [normalizeSmartPunctuation, setNormalizeSmartPunctuation] = useState(true)

  // Deck metadata
  const [deckTitle, setDeckTitle] = useState('Imported deck')
  const [deckDescription, setDeckDescription] = useState('')
  const [deckSubject, setDeckSubject] = useState('')
  const [deckVisibility, setDeckVisibility] = useState<DeckVisibility>('private')
  const [deckClassId] = useState('')
  const [deckTags, setDeckTags] = useState('')

  const [state, setState] = useState<PageState>({ phase: 'idle' })
  const announceRef = useRef<HTMLParagraphElement>(null)

  function announce(msg: string) {
    if (announceRef.current) announceRef.current.textContent = msg
  }

  function handleTabKeyDown(e: React.KeyboardEvent, tab: 'paste' | 'upload') {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      const next = tab === 'paste' ? 'upload' : 'paste'
      setActiveTab(next)
      const ref = next === 'paste' ? pasteTabRef : uploadTabRef
      ref.current?.focus()
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target?.result
      if (typeof text === 'string') setInputText(text)
    }
    reader.readAsText(file, 'utf-8')
  }

  function handleAutoDetect() {
    if (!inputText.trim()) return
    const cfg = suggestConfig(inputText)
    setCardSeparator(cfg.cardSeparator)
    setFieldDelimiter(cfg.fieldDelimiter)
    setNormalizeSmartPunctuation(cfg.normalizeSmartPunctuation)
  }

  const handlePreview = useCallback(async () => {
    if (!firebaseUser) return
    setState({ phase: 'previewing' })
    announce('Loading preview…')
    const snapshot: PreviewSnapshot = {
      input: inputText,
      config: { cardSeparator, fieldDelimiter, normalizeSmartPunctuation },
    }
    try {
      const idToken = await firebaseUser.getIdToken()
      const preview = await previewImport(idToken, {
        input: snapshot.input,
        config: snapshot.config,
      })
      setState({ phase: 'previewed', preview, snapshot })
      announce('Preview ready')
    } catch (err) {
      const msg = err instanceof ImportApiError ? err.message : 'Preview failed. Please try again.'
      setState({ phase: 'error', message: msg })
      announce(msg)
    }
  }, [firebaseUser, inputText, cardSeparator, fieldDelimiter, normalizeSmartPunctuation])

  const handleCommit = useCallback(async () => {
    if (!firebaseUser || state.phase !== 'previewed') return
    const { preview, snapshot } = state
    setState({ phase: 'committing', preview, snapshot })
    announce('Importing…')
    try {
      const idToken = await firebaseUser.getIdToken()
      const result = await commitImport(idToken, {
        input: snapshot.input,
        config: snapshot.config,
        deck: {
          title: deckTitle.trim() || 'Imported deck',
          description: deckDescription,
          subject: deckSubject.trim() || null,
          visibility: deckVisibility,
          classId: deckClassId.trim() || null,
          tags: deckTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        },
      })
      setState({ phase: 'committed', deckId: result.deckId, cardCount: result.cardCount })
      announce(`Done. ${result.cardCount} cards imported.`)
    } catch (err) {
      const msg = err instanceof ImportApiError ? err.message : 'Import failed. Please try again.'
      setState({ phase: 'error', message: msg })
      announce(msg)
    }
  }, [
    firebaseUser,
    state,
    deckTitle,
    deckDescription,
    deckSubject,
    deckVisibility,
    deckClassId,
    deckTags,
  ])

  const isPreviewing = state.phase === 'previewing'
  const isCommitting = state.phase === 'committing'
  const hasPreviewed = state.phase === 'previewed' || state.phase === 'committing'
  const preview = hasPreviewed ? (state as { preview: ImportPreviewResponse }).preview : null

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Import from Quizlet</h1>

      <p role="status" aria-live="polite" className={styles.srOnly} ref={announceRef} />

      {/* ── Source tabs ── */}
      <section className={styles.section} aria-labelledby="source-heading">
        <h2 className={styles.sectionHeading} id="source-heading">
          Source
        </h2>

        <div role="tablist" aria-label="Input source" className={styles.tabList}>
          <button
            role="tab"
            id="tab-paste"
            aria-selected={activeTab === 'paste'}
            aria-controls="tabpanel-paste"
            tabIndex={activeTab === 'paste' ? 0 : -1}
            ref={pasteTabRef}
            className={[styles.tab, activeTab === 'paste' ? styles.tabActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab('paste')}
            onKeyDown={(e) => handleTabKeyDown(e, 'paste')}
          >
            Paste text
          </button>
          <button
            role="tab"
            id="tab-upload"
            aria-selected={activeTab === 'upload'}
            aria-controls="tabpanel-upload"
            tabIndex={activeTab === 'upload' ? 0 : -1}
            ref={uploadTabRef}
            className={[styles.tab, activeTab === 'upload' ? styles.tabActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab('upload')}
            onKeyDown={(e) => handleTabKeyDown(e, 'upload')}
          >
            Upload file
          </button>
        </div>

        <div
          role="tabpanel"
          id="tabpanel-paste"
          aria-labelledby="tab-paste"
          hidden={activeTab !== 'paste'}
          className={styles.tabPanel}
        >
          <label className={styles.label} htmlFor="quizlet-paste">
            Paste your Quizlet export
          </label>
          <textarea
            id="quizlet-paste"
            className={styles.textarea}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            rows={10}
            spellCheck={false}
            aria-label="Quizlet export"
          />
        </div>

        <div
          role="tabpanel"
          id="tabpanel-upload"
          aria-labelledby="tab-upload"
          hidden={activeTab !== 'upload'}
          className={styles.tabPanel}
        >
          <label className={styles.label} htmlFor="quizlet-file">
            Upload a .txt, .csv, or .tsv file
          </label>
          <input
            id="quizlet-file"
            type="file"
            accept=".txt,.csv,.tsv"
            className={styles.fileInput}
            onChange={handleFileChange}
          />
          {inputText && activeTab === 'upload' && (
            <p className={styles.fileLoaded}>
              File loaded — {inputText.length.toLocaleString()} characters.
            </p>
          )}
        </div>
      </section>

      {/* ── Delimiter settings ── */}
      <section className={styles.section} aria-labelledby="delimiters-heading">
        <h2 className={styles.sectionHeading} id="delimiters-heading">
          Delimiters
        </h2>

        <div className={styles.delimiterRow}>
          <div className={styles.selectGroup}>
            <label className={styles.label} htmlFor="card-separator">
              Card separator
            </label>
            <select
              id="card-separator"
              className={styles.select}
              value={cardSeparator}
              onChange={(e) => setCardSeparator(e.target.value as CardSeparator)}
            >
              <option value="double-newline">Double newline</option>
              <option value="semicolon-pair">;; (semicolon pair)</option>
              <option value="newline">Single newline</option>
            </select>
          </div>

          <div className={styles.selectGroup}>
            <label className={styles.label} htmlFor="field-delimiter">
              Field delimiter
            </label>
            <select
              id="field-delimiter"
              className={styles.select}
              value={fieldDelimiter}
              onChange={(e) => setFieldDelimiter(e.target.value as FieldDelimiter)}
            >
              <option value="tab">Tab</option>
              <option value="hyphen">Hyphen ( - )</option>
              <option value="colon-space">Colon-space ( : )</option>
              <option value="pipe">Pipe ( | )</option>
              <option value="comma">Comma (CSV)</option>
            </select>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={normalizeSmartPunctuation}
                onChange={(e) => setNormalizeSmartPunctuation(e.target.checked)}
                className={styles.checkbox}
              />
              Normalize curly quotes and em-dashes
            </label>
          </div>

          <Button variant="secondary" size="sm" onClick={handleAutoDetect} type="button">
            Auto-detect
          </Button>
        </div>
      </section>

      {/* ── Preview / action ── */}
      {!hasPreviewed && (
        <div className={styles.actionRow}>
          <Button
            onClick={() => {
              void handlePreview()
            }}
            loading={isPreviewing}
            disabled={!inputText.trim() || isPreviewing}
          >
            Preview
          </Button>
        </div>
      )}

      {/* ── Preview table ── */}
      {preview && (
        <section className={styles.section} aria-labelledby="preview-heading">
          <h2 className={styles.sectionHeading} id="preview-heading">
            Preview
          </h2>

          {preview.warnings.length > 0 && (
            <ul className={styles.warningList} aria-label="Parse warnings">
              {preview.warnings.map((w, i) => (
                <li key={i} className={styles.warningItem}>
                  {formatWarning(w)}
                </li>
              ))}
            </ul>
          )}

          <div className={styles.tableWrapper}>
            <table className={styles.previewTable}>
              <caption>
                First {preview.cards.length} cards (of {preview.totalCardsBeforeCap})
              </caption>
              <thead>
                <tr>
                  <th scope="col">Term</th>
                  <th scope="col">Definition</th>
                </tr>
              </thead>
              <tbody>
                {preview.cards.map((card, i) => (
                  <tr key={i}>
                    <td>{card.term}</td>
                    <td>{card.definition}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Deck metadata ── */}
          <div className={styles.metadataGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="deck-title">
                Deck title <span aria-hidden="true">*</span>
              </label>
              <input
                id="deck-title"
                type="text"
                required
                className={styles.textInput}
                value={deckTitle}
                onChange={(e) => setDeckTitle(e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="deck-description">
                Description
              </label>
              <input
                id="deck-description"
                type="text"
                className={styles.textInput}
                value={deckDescription}
                onChange={(e) => setDeckDescription(e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="deck-subject">
                Subject
              </label>
              <input
                id="deck-subject"
                type="text"
                className={styles.textInput}
                value={deckSubject}
                onChange={(e) => setDeckSubject(e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="deck-visibility">
                Visibility
              </label>
              <select
                id="deck-visibility"
                className={styles.select}
                value={deckVisibility}
                onChange={(e) => setDeckVisibility(e.target.value as DeckVisibility)}
              >
                <option value="private">Private</option>
                <option value="class">Class</option>
                <option value="domain">Domain</option>
              </select>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="deck-tags">
                Tags (comma-separated)
              </label>
              <input
                id="deck-tags"
                type="text"
                className={styles.textInput}
                value={deckTags}
                onChange={(e) => setDeckTags(e.target.value)}
                placeholder="e.g. biology, chapter-3"
              />
            </div>
          </div>

          <div className={styles.actionRow}>
            <Button
              onClick={() => {
                void handleCommit()
              }}
              loading={isCommitting}
              disabled={isCommitting || !deckTitle.trim()}
            >
              Confirm import
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                void handlePreview()
              }}
              disabled={isPreviewing || isCommitting}
              loading={isPreviewing}
            >
              Re-preview
            </Button>
          </div>
        </section>
      )}

      {/* ── Committed ── */}
      {state.phase === 'committed' && (
        <section className={styles.section} aria-labelledby="committed-heading">
          <h2 className={styles.sectionHeading} id="committed-heading">
            Import complete
          </h2>
          <p className={styles.successMsg}>
            {state.cardCount} {state.cardCount === 1 ? 'card' : 'cards'} imported successfully.
          </p>
          <Link to={`/teacher/decks/${state.deckId}`} className={styles.openLink}>
            Open the new deck
          </Link>
        </section>
      )}

      {/* ── Error ── */}
      {state.phase === 'error' && (
        <div className={styles.errorBox} role="alert">
          <p>{state.message}</p>
          <Button variant="secondary" size="sm" onClick={() => setState({ phase: 'idle' })}>
            Start over
          </Button>
        </div>
      )}
    </div>
  )
}

function formatWarning(w: ParseWarning): string {
  switch (w.code) {
    case 'EMPTY_FIELD':
      return `Line ${w.line}: empty term or definition — card skipped.`
    case 'DUPLICATE_TERM':
      return `Duplicate term "${w.term}" on lines ${w.lines.join(', ')}.`
    case 'TRAILING_TEXT':
      return `Line ${w.line}: extra columns ignored.`
    case 'TRUNCATED_AT_CAP':
      return `Import capped at ${w.cap} cards (${w.total} total found).`
  }
}
